<template>
  <div class="to-do-assign__wrapper">
    <Field
      v-slot="{ errors }"
      as="div"
      name="toDoOwner"
      :rules="rules"
      :modelValue="!!selectedUsers?.length"
      :validateOnBlur="false"
      class="to-do-assign__owner"
    >
      <BaseSubHeading
        size="large"
        weight="400"
        :color="errors[0] ? 'serviceAlert-02' : 'monochrome-06'"
        class="to-do-assign__owner__heading"
      >
        Assigned to:
      </BaseSubHeading>
      <TeamMemberSelect
        id="to-do-assign"
        :users="users"
        :teams="searchedTeams"
        :selectedUsers="selectedUsers"
        :selectedTeams="selectedTeams"
        :selectedTeamList="selectedTeamList"
        :allowTeamSelection="!toDo.disableSelectionTeam"
        :searchText="filterUsers.text"
        :isLast="isLast"
        className="to-do-assign"
        popperClass="to-do-assign__dropdown"
        @selectUser="onUpdateSelectedList"
        @selectTeam="onSelectTeam"
        @removeUser="onRemoveUser"
        @toggleList="toggleList"
        @search="onSearchUsers({ value: $event })"
        @scroll="onScroll"
      >
        <template #label="{ selectedUsers: users, removeUser }">
          <AvatarGroupToDo
            :avatars="users"
            :numberOfVisibleUsers="5"
            :showRemoveButton="true"
            :scaleByHover="true"
            type="avatarGroup"
            size="md"
            @onRemoveAvatar="removeUser"
          />
          <button
            v-show="users?.length"
            class="to-do__plus"
          >
            <icon
              data="@icon/plus.svg"
              :fill="true"
              width="var(--icon-16)"
              height="var(--icon-16)"
              class="to-do__plus-icon"
            />
          </button>
        </template>
      </TeamMemberSelect>
      <div
        v-if="errors[0]"
        class="error-text error-text-small"
      >
        required
      </div>
    </Field>
    <div
      v-if="createdBy.id && currentUser.id !== createdBy.id"
      class="to-do-assign__created-by"
    >
      <BaseSubHeading
        size="large"
        weight="400"
        color="monochrome-05"
        darkColor="monochrome-03"
      >
        From:
      </BaseSubHeading>
      <div class="to-do-assign__created-by__user">
        <Avatar
          :src="createdBy.pictureUrl ?? null"
          :gap="createdBy.getInitials()"
          bordered
        />
        <BaseSubHeading
          size="large"
          weight="500"
          color="monochrome-06"
        >
          {{ createdBy.username }}
        </BaseSubHeading>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { mixins, Options } from "vue-class-component"
import { Prop } from "vue-property-decorator/lib/decorators/Prop"
import { Action, Getter } from "s-vuex-class"
import { ErrorMessage, Field } from "vee-validate"

import AvatarGroupToDo from "@/components/Avatar/AvatarGroupToDo.vue"
import Avatar from "@/components/Avatar/index.vue"
import { TeamMemberSelect } from "@/components/User"

import Debounce from "@/decorators/Debounce"
import { hideDropdownToScroll } from "@/mixins/hideDropdownToScroll"
import { FilterTeam, FilterUser, Project, ServerResponse, Team } from "@/models"
import { ToDo } from "@/models/ToDo"
import { User } from "@/models/User"
import { Actions, Getters } from "@/store"
import { parsingServerResponseContent } from "@/utils"

@Options({
  name: "ToDoSelectUser",
  components: {
    TeamMemberSelect,
    AvatarGroupToDo,
    Field,
    ErrorMessage,
    Avatar
  }
})
export default class ToDoSelectUser extends mixins(hideDropdownToScroll) {
  @Getter(Getters.GET_USER_CURRENT_PROFILE) readonly currentUser: User
  @Getter(Getters.GET_PROJECT) readonly project: Project

  @Action(Actions.GET_USERS) getUsers: (filter: FilterUser) => Promise<ServerResponse<User>>
  @Action(Actions.TEAMS_WITH_USERS) searchTeam: (
    params: FilterTeam
  ) => Promise<ServerResponse<Team>>
  @Prop({ required: true }) readonly toDo: ToDo
  @Prop({ default: "" }) readonly rules: () => boolean | string

  filterUsers = new FilterUser({
    page: 1,
    size: 100,
    forSearch: true,
    includeEnabledOnly: true
  })
  filterTeam = new FilterTeam({
    forSearch: true,
    excludeEmptyTeams: true,
    enabled: true
  })
  selectedTeamList = false
  searchedTeams: Team[] = []
  users: User[] = []
  isLastForTeams = true
  isLastForUsers = true

  get createdBy() {
    return this.toDo.createdBy
  }

  get selectedUsers() {
    return this.toDo.owners
  }

  set selectedUsers(val) {
    this.$emit("onSelectedUsers", val)
  }

  get selectedTeams() {
    return this.toDo.selectedTeams
  }

  set selectedTeams(val) {
    this.$emit("onSelectedTeams", val)
  }

  get isLast() {
    return this.selectedTeamList ? this.isLastForTeams : this.isLastForUsers
  }

  async mounted() {
    this.filterUsers.teamId = this.toDo.filterByTeamId
    this.filterUsers.addSort("firstName_keyword", true)
    if (this.toDo.disableSelectionTeam) {
      this.filterUsers.includeDemo = this.project?.demo ?? false
      await this.onGetUsers()
    } else {
      await Promise.all([this.onGetUsers(), this.onGetTeams()])
    }
    this.selectedTeams.forEach(team => {
      const { users } = team
      this.selectedUsers = users
    })
  }

  getSelectedItemIndex(value: Team | User) {
    const listOfItems = value instanceof Team ? this.selectedTeams : this.selectedUsers
    return listOfItems.findIndex(item => item.id === value.id)
  }

  onUpdateSelectedList(value: User) {
    const index = this.getSelectedItemIndex(value)
    if (index === -1) {
      this.selectedUsers.push(value)
    } else {
      this.selectedUsers.splice(index, 1)
    }
  }

  checkSelectedTeam() {
    this.selectedTeams = this.selectedTeams.filter(team => {
      const { users } = team
      return users.some(user =>
        this.selectedUsers.find(selectedUser => selectedUser.id === user.id)
      )
    })
  }

  onSelectTeam(team: Team) {
    const { users } = team
    // This is a necessary to avoid the issue with the same users in the list
    if (users.length) {
      this.selectedUsers = this.selectedUsers.filter(
        item => !users.find(user => user.id === item.id)
      )
    }
    if (this.getSelectedItemIndex(team) === -1) {
      this.selectedUsers = [...this.selectedUsers, ...users]
    }
    const index = this.getSelectedItemIndex(team)
    if (index === -1) {
      this.selectedTeams.push(team)
    } else {
      this.selectedTeams.splice(index, 1)
    }
  }

  toggleList() {
    this.selectedTeamList = !this.selectedTeamList
    this.$nextTick(() => {
      this.checkSelectedTeam()
    })
  }

  onScroll() {
    if (this.selectedTeamList) {
      this.filterTeam.page++
      this.onGetTeams()
    } else {
      this.filterUsers.page++
      this.onGetUsers()
    }
  }

  async onGetTeams() {
    const serverResponseTeam = await this.searchTeam(this.filterTeam)
    this.isLastForTeams = serverResponseTeam.last
    this.searchedTeams = parsingServerResponseContent({
      originalContent: this.searchedTeams,
      serverResponseContent: serverResponseTeam.content.filter(team => team.users.length),
      numberOfPage: serverResponseTeam.pageable.pageNumber
    })
  }

  async onGetUsers() {
    const serverResponseUser = await this.getUsers(this.filterUsers)
    this.isLastForUsers = serverResponseUser.last
    this.users = parsingServerResponseContent({
      originalContent: this.users,
      serverResponseContent: serverResponseUser.content,
      numberOfPage: serverResponseUser.pageable.pageNumber
    })
  }

  onRemoveUser(user: User) {
    this.selectedUsers = this.selectedUsers.filter(item => item.id !== user.id)
  }

  @Debounce(500)
  async onSearchUsers({ value }: { value: string }) {
    this.filterUsers.text = value
    this.filterUsers.page = 1
    await this.onGetUsers()
  }
}
</script>
